import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { 
  Plus, Edit2, Trash2, Globe, Palette, Star, ExternalLink, 
  Loader2, CheckCircle, XCircle, Building2, Upload, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { api, getBackendUrl } from '../../lib/api';

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    logo_url: '',
    primary_color: '#0F2744',
    secondary_color: '#4C8F6E',
    is_active: true
  });

  useEffect(() => {
    fetchBrands();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await api.get('/brands');
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
      } else {
        throw new Error('Failed to fetch brands');
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Erreur lors du chargement des marques');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingBrand(null);
    setFormData({
      name: '',
      domain: '',
      logo_url: '',
      primary_color: '#0F2744',
      secondary_color: '#4C8F6E',
      is_active: true
    });
    setShowDialog(true);
  };

  const openEditDialog = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      domain: brand.domain || '',
      logo_url: brand.logo_url || '',
      primary_color: brand.primary_color || '#0F2744',
      secondary_color: brand.secondary_color || '#4C8F6E',
      is_active: brand.is_active !== false
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.domain) {
      toast.error('Le nom et le domaine sont requis');
      return;
    }

    try {
      setSaving(true);
      let response;
      
      if (editingBrand) {
        // Update
        response = await api.put(`/admin/brands/${editingBrand.id}`, formData);
      } else {
        // Create
        response = await api.post('/admin/brands', formData);
      }
      
      if (response.ok) {
        toast.success(editingBrand ? 'Marque mise à jour' : 'Marque créée');
        setShowDialog(false);
        fetchBrands();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving brand:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez PNG, JPEG, SVG ou WebP');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Max 5MB');
      return;
    }
    
    // If editing an existing brand, upload immediately
    if (editingBrand) {
      setUploading(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await fetch(`${getBackendUrl()}/api/admin/brands/${editingBrand.id}/upload-logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          },
          body: formDataUpload
        });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
          setFormData({ ...formData, logo_url: data.logo_url });
          toast.success('Logo téléchargé');
          fetchBrands();
        } else {
          toast.error(data.detail || 'Erreur lors du téléchargement');
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
        toast.error('Erreur lors du téléchargement');
      } finally {
        setUploading(false);
      }
    } else {
      // For new brand, we need to create the brand first, then upload
      // For now, show a preview and store the file
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({ ...formData, logo_url: e.target.result, logo_file: file });
      };
      reader.readAsDataURL(file);
      toast.info('Logo prévisualisé. Sauvegardez la marque pour télécharger.');
    }
  };

  const handleDelete = async (brand) => {
    if (!window.confirm(`Supprimer la marque "${brand.name}" ?`)) return;
    
    try {
      const response = await api.delete(`/admin/brands/${brand.id}`);
      if (response.ok) {
        toast.success('Marque supprimée');
        fetchBrands();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSetDefault = async (brand) => {
    try {
      const response = await api.post(`/admin/brands/${brand.id}/set-default`);
      if (response.ok) {
        toast.success(`"${brand.name}" définie comme marque par défaut`);
        fetchBrands();
      } else {
        toast.error('Erreur lors de la définition par défaut');
      }
    } catch (error) {
      console.error('Error setting default brand:', error);
      toast.error('Erreur lors de la définition par défaut');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-6 lg:px-8 py-4 md:py-6" data-testid="brands-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-display font-bold text-tp-text">Gestion des Marques</h1>
          <p className="text-tp-text-light mt-1 text-sm md:text-base">
            Configurez les marques pour le mode white-label
          </p>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-tp-primary hover:bg-tp-primary-light text-white w-full sm:w-auto"
          data-testid="create-brand-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle marque
        </Button>
      </div>

      {/* Brands Grid */}
      {brands.length === 0 ? (
        <Card className="border-tp-border mx-0">
          <CardContent className="p-4 md:p-8 text-center">
            <Building2 className="h-12 w-12 text-tp-text-light mx-auto mb-4" />
            <h3 className="text-lg font-display font-semibold text-tp-text mb-2">
              Aucune marque configurée
            </h3>
            <p className="text-tp-text-light mb-4">
              Créez votre première marque pour activer le mode white-label
            </p>
            <Button 
              onClick={openCreateDialog}
              className="bg-tp-accent hover:bg-tp-accent-light text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Créer une marque
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {brands.map((brand) => (
            <Card 
              key={brand.id} 
              className={`border-tp-border hover:shadow-md transition-shadow ${!brand.is_active ? 'opacity-60' : ''}`}
              data-testid={`brand-card-${brand.id}`}
            >
              <CardHeader className="pb-3 px-4 md:px-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {/* Color Preview */}
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ backgroundColor: brand.primary_color }}
                    >
                      {brand.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-base md:text-lg font-display text-tp-text flex flex-wrap items-center gap-2">
                        <span className="truncate">{brand.name}</span>
                        {brand.is_default && (
                          <Badge className="bg-tp-accent text-white text-xs flex-shrink-0">Par défaut</Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-1 text-tp-text-light text-sm mt-1">
                        <Globe className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{brand.domain}</span>
                      </div>
                    </div>
                  </div>
                  {brand.is_active ? (
                    <Badge className="bg-green-100 text-green-700 flex-shrink-0 self-start">Actif</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 flex-shrink-0 self-start">Inactif</Badge>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4 px-4 md:px-6">
                {/* Colors */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border border-gray-200"
                      style={{ backgroundColor: brand.primary_color }}
                    />
                    <span className="text-xs text-tp-text-light">Primaire</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border border-gray-200"
                      style={{ backgroundColor: brand.secondary_color }}
                    />
                    <span className="text-xs text-tp-text-light">Secondaire</span>
                  </div>
                </div>

                {/* Logo URL */}
                {brand.logo_url && (
                  <div className="flex items-center gap-2 text-sm text-tp-text-light">
                    <ExternalLink className="h-3 w-3" />
                    <span className="truncate">{brand.logo_url}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-tp-border">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openEditDialog(brand)}
                    className="flex-1 border-tp-border"
                    data-testid={`edit-brand-${brand.id}`}
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Modifier
                  </Button>
                  {!brand.is_default && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSetDefault(brand)}
                        className="border-tp-border"
                        title="Définir par défaut"
                      >
                        <Star className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDelete(brand)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`delete-brand-${brand.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingBrand ? 'Modifier la marque' : 'Nouvelle marque'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la marque *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Talent Partner, RecrutPro..."
                data-testid="brand-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">Domaine *</Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                placeholder="Ex: talentpartner.com, recrutpro.fr..."
                data-testid="brand-domain-input"
              />
              <p className="text-xs text-tp-text-light">
                Le domaine sera automatiquement normalisé (sans http://, www., etc.)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Logo de la marque</Label>
              <div className="flex items-center gap-4">
                {/* Logo Preview */}
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                  {formData.logo_url ? (
                    <img 
                      src={formData.logo_url.startsWith('data:') ? formData.logo_url : (formData.logo_url.startsWith('http') ? formData.logo_url : getBackendUrl() + formData.logo_url)}
                      alt="Logo preview"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Image className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                
                {/* Upload Button */}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Téléchargement...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Télécharger un logo
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-tp-text-light mt-1">
                    PNG, JPEG, SVG ou WebP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary_color">Couleur primaire</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="primary_color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="flex-1"
                    data-testid="brand-primary-color"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="secondary_color">Couleur secondaire</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="secondary_color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                  />
                  <Input
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="flex-1"
                    data-testid="brand-secondary-color"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <Label htmlFor="is_active">Marque active</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="brand-active-switch"
              />
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg border border-tp-border bg-gray-50">
              <p className="text-xs text-tp-text-light mb-2">Aperçu</p>
              <div 
                className="p-3 rounded flex items-center gap-3"
                style={{ backgroundColor: formData.primary_color }}
              >
                <div 
                  className="w-8 h-8 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: formData.secondary_color }}
                >
                  {formData.name ? formData.name.charAt(0).toUpperCase() : 'M'}
                </div>
                <span className="text-white font-medium">
                  {formData.name || 'Nom de la marque'}
                </span>
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Annuler</Button>
              </DialogClose>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-tp-primary hover:bg-tp-primary-light text-white"
                data-testid="save-brand-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {editingBrand ? 'Mettre à jour' : 'Créer'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandsPage;
